import { useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ArchiveBoxIcon, ArrowRightOnRectangleIcon, ArrowTrendingUpIcon, BanknotesIcon, CalendarDaysIcon, CalendarIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, Cog6ToothIcon, CurrencyDollarIcon, DocumentCheckIcon, DocumentTextIcon, ExclamationTriangleIcon, PhoneIcon, QuestionMarkCircleIcon, Squares2X2Icon, UserCircleIcon, UserGroupIcon, UsersIcon } from '@heroicons/react/24/outline';
import { MigroLogo } from '@/components/common/MigroLogo';
import { SidebarBadge } from '@/components/ui/SidebarBadge';
import { useAuth } from '@/providers/AuthProvider';
import { usePermissions } from '@/hooks/usePermissions';
import { expedientesListQueryKey } from '@/hooks/useExpedientes';
import { useSidebarCounters } from '@/hooks/useSidebarCounters';
import { useFollowUpsAvailable } from '@/hooks/useFollowUpsAvailable';
import { expedienteApi } from '@/services/expedienteApi';
import type { ExpedienteListResponse } from '@/types/expediente';
import type { SidebarCounterKey } from '@/types/sidebarCounters';

type NavItem = {
  name: string;
  href: string;
  icon: typeof Squares2X2Icon;
  adminOnly?: boolean;
  /** Key del sidebar-counters response — muestra badge circular al lado. */
  counterKey?: SidebarCounterKey;
};

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/crm', icon: Squares2X2Icon },
  // "Novedades" eliminado del sidebar (2026-04-24) — la campanita en el
  // header ya cumple la función. La ruta /crm/activity-center sigue
  // accesible desde el footer "Ver todo el historial" del dropdown del
  // NotificationBell.
  { name: 'Seguimientos', href: '/crm/seguimientos', icon: ArrowTrendingUpIcon, counterKey: 'seguimientos' },
  { name: 'Contactos', href: '/crm/contacts', icon: UsersIcon },
  { name: 'Oportunidades', href: '/crm/opportunities', icon: ArrowTrendingUpIcon, counterKey: 'oportunidades' },
  // Contratos subido aquí (2026-05-12) para que sea accesible en el primer
  // pantallazo del sidebar en mobile (drawer corto).
  { name: 'Contratos', href: '/crm/contracts', icon: DocumentCheckIcon },
  { name: 'Citas', href: '/crm/appointments', icon: CalendarDaysIcon, counterKey: 'citas' },
  { name: 'Tareas', href: '/crm/tasks', icon: CheckIcon, counterKey: 'tareas' },
  { name: 'Notas', href: '/crm/notes', icon: DocumentTextIcon },
  { name: 'Calendario', href: '/crm/calendar', icon: CalendarIcon },
  { name: 'Llamadas', href: '/crm/call', icon: PhoneIcon },
  { name: 'Grabaciones', href: '/crm/grabaciones', icon: ArchiveBoxIcon },
  { name: 'Sesiones de trabajo', href: '/crm/sesion', icon: UserGroupIcon },
  { name: 'Historial', href: '/crm/history', icon: ClockIcon },
  { name: 'Presupuestos', href: '/crm/presupuestos', icon: CurrencyDollarIcon },
  { name: 'Facturas', href: '/crm/facturas', icon: BanknotesIcon },
  { name: 'Expedientes', href: '/crm/expedientes', icon: DocumentTextIcon },
  { name: 'Incidencias', href: '/crm/incidencias', icon: ExclamationTriangleIcon, counterKey: 'incidencias' },
  { name: 'FAQs', href: '/crm/faqs', icon: QuestionMarkCircleIcon },
  { name: 'Mi Perfil', href: '/crm/profile', icon: UserCircleIcon },
  { name: 'Configuración', href: '/crm/settings', icon: Cog6ToothIcon },
];

interface CRMSidebarProps {
  className?: string;
  onClose?: () => void;
  isCollapsed?: boolean;
  onToggle?: () => void;
}

const PAGE_SIZE = 20;

export function CRMSidebar({ className, onClose, isCollapsed = false, onToggle }: CRMSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, logout, user } = useAuth();
  const queryClient = useQueryClient();

  const handleLogout = async () => {
    onClose?.();
    await logout();
    navigate('/auth/login');
  };
  const { canViewAllExpedientes } = usePermissions();

  const prefetchExpedientes = useCallback(() => {
    const filters = canViewAllExpedientes() ? { scope: 'all' as const } : {};
    const queryKey = expedientesListQueryKey(filters, PAGE_SIZE);
    void queryClient.prefetchInfiniteQuery({
      queryKey,
      queryFn: async ({ pageParam }) =>
        expedienteApi.list({ ...filters, skip: pageParam as number, limit: PAGE_SIZE }),
      initialPageParam: 0,
      getNextPageParam: (lastPage: ExpedienteListResponse) => {
        const nextSkip = lastPage.skip + lastPage.items.length;
        return lastPage.items.length >= PAGE_SIZE && nextSkip < lastPage.total ? nextSkip : undefined;
      },
    });
  }, [queryClient, canViewAllExpedientes]);

  // Defensive probe (audit H-4 / C-3): si el endpoint follow-ups/board
  // está apagado en backend (FOLLOW_UPS_V2=False), ocultamos "Seguimientos"
  // para que el agente no haga click en un link roto. Default true.
  const followUpsAvailable = useFollowUpsAvailable();

  // Filtrar navegación según permisos + features disponibles
  const filteredNavigation = navigation.filter(item => {
    if (item.adminOnly && !isAdmin) {
      return false;
    }
    if (item.href === '/crm/seguimientos' && !followUpsAvailable) {
      return false;
    }
    return true;
  });

  // Badges: un único roundtrip polled cada 60s con los counters de
  // todos los módulos relevantes (novedades, seguimientos, tareas, etc.).
  const { data: counters } = useSidebarCounters();

  return (
    <div className={cn("flex flex-col flex-grow bg-white dark:bg-gray-900 pt-5 pb-4 overflow-y-auto h-full relative", className)}>
      {/* Header con logo y botón toggle */}
      <div className={cn(
        "flex items-center flex-shrink-0 mb-5 transition-all duration-300 relative",
        isCollapsed ? "px-2 justify-center" : "px-4"
      )}>
        {!isCollapsed && (
          <div className="flex items-center flex-1">
            <MigroLogo variant="sidebar" />
            <span className="ml-2 text-xl font-display font-bold text-primary">
              CRM
            </span>
          </div>
        )}
        {isCollapsed && (
          <MigroLogo variant="sidebar" />
        )}
        {onToggle && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className={cn(
              "h-8 w-8 flex-shrink-0 hover:bg-gray-100 dark:hover:bg-gray-800",
              isCollapsed ? "absolute top-2 right-0" : "ml-2"
            )}
            title={isCollapsed ? "Expandir sidebar" : "Colapsar sidebar"}
          >
            {isCollapsed ? (
              <ChevronRightIcon className="h-4 w-4" />
            ) : (
              <ChevronLeftIcon className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>

      {/* Navegación */}
      <nav className={cn(
        "mt-5 flex-1 space-y-1",
        isCollapsed ? "px-2" : "px-2"
      )}>
        {filteredNavigation.map((item) => {
          const isActive = location.pathname === item.href || 
                          (item.href !== '/crm' && location.pathname.startsWith(item.href));
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onClose}
              onMouseEnter={item.href === '/crm/expedientes' ? prefetchExpedientes : undefined}
              onFocus={item.href === '/crm/expedientes' ? prefetchExpedientes : undefined}
              className={cn(
                isActive
                  ? 'bg-primary/10 text-primary font-semibold'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100',
                'group flex items-center text-sm font-medium rounded-md transition-colors relative',
                isCollapsed ? 'justify-center px-2 py-2' : 'px-3 py-2'
              )}
              title={isCollapsed ? item.name : undefined}
            >
              <item.icon
                className={cn(
                  isActive ? 'text-primary' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400',
                  'flex-shrink-0 h-5 w-5 transition-colors',
                  !isCollapsed && 'mr-3'
                )}
                aria-hidden="true"
              />
              {!isCollapsed && (
                <span className="flex-1">{item.name}</span>
              )}
              {item.counterKey && counters?.[item.counterKey] && (
                <SidebarBadge
                  count={counters[item.counterKey].count}
                  severity={counters[item.counterKey].severity}
                  className={cn(isCollapsed && 'absolute top-0 right-0 scale-75')}
                />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer: user info + logout
          Visible siempre; en mobile (drawer abierto desde hamburguesa) es la
          única vía para cerrar sesión, ya que el header móvil no tiene el
          botón Logout. */}
      {!isCollapsed && (
        <div className="mt-auto flex-shrink-0 border-t border-gray-200 dark:border-gray-800 pt-3 px-2 pb-2">
          {user && (
            <div className="px-3 pb-2">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {user.full_name || user.email || 'Usuario'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user.role === 'lawyer' ? 'Abogado'
                  : user.role === 'agent' ? 'Agente'
                  : user.role === 'admin' ? 'Administrador'
                  : 'Usuario'}
              </p>
            </div>
          )}
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start gap-3 px-3 py-2 h-auto text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            <span>Cerrar Sesión</span>
          </Button>
        </div>
      )}
      {isCollapsed && (
        <div className="mt-auto flex-shrink-0 border-t border-gray-200 dark:border-gray-800 pt-3 px-2 pb-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="w-full justify-center h-9 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
            title="Cerrar Sesión"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
          </Button>
        </div>
      )}
    </div>
  );
}

